import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GlobalConstants } from '../constant/constant';
import { StoreName } from '../entities/StoreName';
import { DeviceInfoService } from './device-info.service';
import { User } from '../entities/User';
import { addWatermarkToBadgePhoto, convertBlobToBase64 } from '../utils/form-helper-functions';
import { DataService } from './data.service';
import { PhotoService } from './photo.service';
import { DbTransaction } from '../models/db/db-transaction';
import { RecordNotFoundException, RecordSoftDeletedException, StateValidationException } from '../utils/exceptions';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { Check, EntityHelper } from '../utils/entity-helper';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { Subject } from 'rxjs';
import { Photo } from '../entities/photo';

@Injectable({
  providedIn: 'root',
})
export class UserService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<User>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor(private deviceInfoService: DeviceInfoService, private photoService: PhotoService) {
    super();
  }

  // #region Utility

  emitChange(user: User, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: user, who: who, what: what });
  }

  getEmitChangeCallback(user: User, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitchangeForUser + '_' + user.RecordId,
      fn: this.emitChange.bind(this, user, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.User, StoreName.Photo], 'readwrite'));
  }

  // #endregion
  // #region Low-Level Get One

  async getUserByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<User> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId(StoreName.User, onlineId, User, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as User;

    if (this.checkIsUserSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getUserByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<User> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const result = await this.repository.getByLocalId(StoreName.User, recordId, User, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as User;

    if (this.checkIsUserSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-Level Get Many

  async getAllUsers(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<User[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll(StoreName.User, filter, User, transaction);

    let records = result as User[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsUserSoftDeleted(record));
    }

    return records;
  }

  // #endregion
  // #region Low-level Create

  private async createUser(who: DataChangeEmitWho, recordToCreate: User, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateUserCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.User, transaction);

    // LC5. Get created record
    const createdRecord = await this.getUserByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // #region Low-Level Update

  private async updateUser(who: DataChangeEmitWho, recordToUpdate: User, skipLocalCopyValidation: boolean, localCopy: User | null, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getUserByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateUserUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<User>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<User>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateUser(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.User, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getUserByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // #region Low-level Validate Model

  async validateUser(user: User, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  async validateSoftDeletedUser(user: User, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!this.checkIsUserSoftDeleted(user)) {
      throw new StateValidationException('SoftDeleted', user);
    }

    return;
  }

  // #endregion
  // #region Low-Level Validate Action
  async validateUserCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateUserUpdate(existingRecord: User, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }
  // #endregion
  // #region Mid-Level Get One

  /** @deprecated Use AppUserService currentUserId$ or currentUserId instead */
  getCurrentUserId(): number | null {
    const currentUser = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY));
    if (!currentUser) {
      return null;
    }

    return currentUser.Id;
  }

  /** @deprecated Use AppUserService currentUser$ or currentUser instead */
  async getCurrentUser(existingTransaction?: DbTransaction): Promise<User> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const currentUserId = this.getCurrentUserId();

    return await this.getUserByOnlineId(currentUserId, transaction);
  }

  private async getCurrentUserPendingBadgePhoto(existingTransaction?: DbTransaction): Promise<Photo> {
    // const transaction = await this.startTransactionIfNeeded(existingTransaction);

    return await this.photoService.getPendingBadgePhoto();
  }

  // #endregion
  // #region Mid-level Sync

  async upsertUserFromSync(who: DataChangeEmitWho, recordToUpsert: User, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getUserByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      recordToUpsert.BadgePhoto = existingRecord.BadgePhoto; // Preserve local badge photo data which is not returned from sync
      await this.updateUser(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createUser(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  async uploadPendingUserBadge(existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const pendingPhoto = await this.getCurrentUserPendingBadgePhoto(transaction);

    if (pendingPhoto) {
      await this.updateUserBadge(pendingPhoto, DataChangeEmitWho.Self, transaction);
    }
  }

  async updateUserBadge(record: Photo, who: DataChangeEmitWho = DataChangeEmitWho.Sync, existingTransaction?: DbTransaction) {
    const token = await this.photoService.uploadPhoto(record.fileName, record.dataUrl);

    record.uriToken = token;

    const badgePhoto = await this.http.postBlob(this.USER_API_ENDPOINTS.BadgePhoto, { BadgePhotoFilename: record.fileName }).toPromise();

    const unverifiedBadePhotoBase64 = await addWatermarkToBadgePhoto(badgePhoto);

    const transaction = await this.startTransactionIfNeeded();

    const user: User = await this.getCurrentUser(transaction);

    user.BadgePhotoFilename = record.fileName;
    user.IsBadgePhotoVerified = false;
    user.BadgePhoto = unverifiedBadePhotoBase64;

    await this.updateUser(who, user, true, null, transaction);

    await this.photoService.deletePhoto(record);

    this.emitChange(user, DataChangeEmitWho.Sync, DataChangeEmitWhat.Update);
  }

  async syncUserBadge() {
    const user: User = await this.getCurrentUser();
    if (user.BadgePhotoFilename) {
      try {
        const badgePhoto = await this.http.getBlob(this.USER_API_ENDPOINTS.BadgePhoto).toPromise();

        if (badgePhoto) {
          const badgePhotoBase64 = await convertBlobToBase64(badgePhoto);

          if (!user.IsBadgePhotoVerified) {
            const unverifiedBadePhotoBase64 = await addWatermarkToBadgePhoto(badgePhoto);
            user.BadgePhoto = unverifiedBadePhotoBase64;
          } else {
            user.BadgePhoto = badgePhotoBase64;
          }
        }
      } catch {
        console.log('Cannot fetch user badge photo data');
      }
    }

    try {
      const organizationLogo = await this.http.getBlob(this.USER_API_ENDPOINTS.OrganizationLogo).toPromise();

      if (organizationLogo) {
        const organizationLogoBase64 = await convertBlobToBase64(organizationLogo);
        user.OrganizationLogo = organizationLogoBase64;
      }
    } catch {
      console.log('Cannot fetch organization logo data');
    }

    await this.updateUser(DataChangeEmitWho.Sync, user, true, null);
  }

  // #endregion
  // #region Lookup

  async getUserName(userId: number, format = 'First Last', existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<string> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    let user = await this.getUserByOnlineId(userId, transaction, includeDeleted);

    if (!user) {
      throw new RecordNotFoundException(userId);
    }

    if (!includeDeleted && this.checkIsUserSoftDeleted(user)) {
      throw new RecordSoftDeletedException(userId);
    }

    if (format === 'First Last') {
      return user.FirstName + ' ' + user.LastName;
    } else if (format === 'Last, First') {
      return user.LastName + ', ' + user.FirstName;
    } else {
      return user.FirstName + ' ' + user.LastName;
    }
  }

  // #endregion
  // #region Check
  
  async checkIsUserIdCurrentUser(userId: number) {
    return userId === this.getCurrentUserId();
  }

  checkIsUserSoftDeleted(user: User): boolean {
    return user.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: User, b: User): boolean {
    // TODO: Implement checkImmutableFieldsMatch

    return true;
  }

  // #endregion
  // region Other



  // #endregion
  
    getCurrentUserIsActive(): boolean {
    let currentUser = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY));
    return currentUser.IsActive;
  }

}
